import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ResetPassword } from "../../../dataTypes/Auth";
import { ThemeConfig } from "../../../dataTypes/ThemeConfig";
import FormErrorSuccessMsg from "../../shared/formErrorSuccessMsg/FormErrorSuccessMsg";
import "./RecoverPassword.css";

interface RecoverPasswordProps {
  themeConfig: ThemeConfig;
}

export const RecoverPassword: React.FC<RecoverPasswordProps> = ({
  themeConfig,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const { slug } = useParams<{ slug: string }>();

  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();
  const [showMessage, setShowMessage] = useState(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [passwordsMatchError, setPasswordsMatchError] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const token = searchParams.get("token");
  const email = searchParams.get("email");
  //   let check = searchparam.get(token);
  const [payload, setPayload] = useState<ResetPassword>({
    email: "",
    password: "",
    token: "",
  });

  useEffect(() => {
    if (email && token) {
      setPayload((prev) => ({
        ...prev,
        email,
        token,
      }));
    }
  }, [email, token]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPayload((prev) => ({ ...prev, [name]: value }));
    setShowMessage(false);
    setPasswordsMatchError(false);
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(event.target.value);
    setShowMessage(false);
    setPasswordsMatchError(false);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (payload.password !== confirmPassword) {
      setPasswordsMatchError(true);
      return;
    }

    setLoading(true);
    setShowMessage(false);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/${slug}/auth/password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      setShowMessage(true);
      setSuccess(response.ok);

      if (response.ok) {
        setPayload({ email: "", password: "", token: "" });
        setConfirmPassword("");
        setPasswordsMatchError(false);
      }
    } catch (error) {
      console.error("Error", error);
      setShowMessage(true);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <Fragment>
      <Box className="auth">
        <Box width="100%">
          <Box display="flex" alignItems="center" justifyContent="center">
            <img src={themeConfig.logo_img} alt="Logo" />
          </Box>

          <Box>
            {success && (
              <Fragment>
                {" "}
                <FormErrorSuccessMsg
                  message="Senha alterada com sucesso!"
                  success={true}
                />
              </Fragment>
            )}
            {!success && (
              <Fragment>
                <Typography variant="body1">
                  <strong style={{ display: "block", textAlign: "center" }}>
                    Insira sua nova senha
                  </strong>
                </Typography>

                {!success && showMessage && (
                  <FormErrorSuccessMsg
                    message={
                      "Erro ao criar nova senha\n Por favor, tente novamente."
                    }
                    success={false}
                  />
                )}
                {passwordsMatchError && (
                  <FormErrorSuccessMsg
                    message="Erro de incompatibilidade de senha."
                    success={false}
                  />
                )}
                <form onSubmit={onSubmit}>
                  <Box className="form-group">
                    <label htmlFor="password" style={{ fontWeight: 700 }}>
                      Nova Senha
                    </label>
                    <TextField
                      id="outlined-basic-password"
                      name="password"
                      value={payload.password}
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      onChange={handleInputChange}
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "40px",
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "10px 14px",
                          borderRadius: "8px",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box className="form-group">
                    <label htmlFor="confirmPassword">
                      Confirmar Nova Senha
                    </label>
                    <TextField
                      id="outlined-basic-confirm-password"
                      name="confirmPassword"
                      variant="outlined"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root": {
                          height: "40px",
                          fontSize: "0.875rem",
                          borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "10px 14px",
                          borderRadius: "8px",
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      bgcolor: themeConfig.color,
                      marginTop: "16px",
                      borderRadius: "8px",
                      fontWeight: 700,
                      textTransform: "capitalize",
                      fontSize: "18px",
                    }}
                    fullWidth
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Enviar"
                    )}{" "}
                    {/* Show spinner when loading */}
                  </Button>
                </form>
              </Fragment>
            )}
          </Box>

          <Box>
            <Button
              variant="outlined"
              sx={{
                marginTop: "16px",
                color: themeConfig.color,
                borderRadius: "8px",
                textTransform: "capitalize",
              }}
              fullWidth
              onClick={() => navigate(`/${slug}/login`)}
            >
              Ir para login
            </Button>
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
};

export default RecoverPassword;
